import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bot, ChevronDown, ChevronUp, Plus, Check, Sparkles, Trash2, Save, X, AlertCircle, Loader2, FlaskConical, Network, Edit3, Wrench } from 'lucide-react';
import { haptic } from '../../telegram';
import { agentsApi } from '../../api/agentsApi';
import { skillsApi } from '../../api/skillsApi';
import { integrationsApi } from '../../api/integrationsApi';
import { pipelinesApi, type PipelineInstance } from '../../api/pipelinesApi';
import type { BuiltinTool } from '../../api/skillsApi';
import type { IntegrationConnectionDto, McpServerDto } from '../../api/integrationsApi';
import type { Agent, ModelTier, Skill } from '../../types/contracts';
import { ConfirmSheet } from '../ConfirmSheet';

const TIERS: { value: ModelTier; label: string; desc: string }[] = [
  { value: 'brain', label: 'Brain', desc: 'Самый умный — сложные задачи' },
  { value: 'mid', label: 'Mid', desc: 'Баланс — повседневные задачи' },
  { value: 'cheap', label: 'Cheap', desc: 'Быстрый — простые операции' },
];
const TIER_LABEL: Record<string, string> = { brain: 'Brain', mid: 'Mid', cheap: 'Cheap' };
const PROVIDER_LABELS: Record<string, string> = {
  google_sheets: 'Гугл таблицы', gmail: 'Gmail', google_calendar: 'гугл календарь',
  notion: 'Notion', slack: 'Slack', github: 'GitHub',
};

type ImpactPipe = { id: string; name: string | null; enabled: boolean; memberCount?: number; agentChain?: string[] };

export function Agents() {
  const nav = useNavigate();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [pipes, setPipes] = useState<PipelineInstance[]>([]);
  const [availSkills, setAvailSkills] = useState<Skill[]>([]);
  const [builtins, setBuiltins] = useState<BuiltinTool[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [openCard, setOpenCard] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ id: string; ok: boolean; msg: string } | null>(null);
  const [agentConns, setAgentConns] = useState<Record<string, IntegrationConnectionDto[]>>({});
  const [confirmDel, setConfirmDel] = useState<{ agent: Agent; single: ImpactPipe[]; multi: ImpactPipe[] } | null>(null);
  const [pipeChoice, setPipeChoice] = useState<Record<string, 'delete' | 'rebuild'>>({});
  const [deleting, setDeleting] = useState(false);
  const [fName, setFName] = useState('');
  const [fDesc, setFDesc] = useState('');
  const [fPrompt, setFPrompt] = useState('');
  const [fTier, setFTier] = useState<ModelTier>('mid');
  const [fSkills, setFSkills] = useState<string[]>([]);
  const [fTools, setFTools] = useState<string[]>([]);
  const [fConnIds, setFConnIds] = useState<string[]>([]);
  const [connCatalog, setConnCatalog] = useState<IntegrationConnectionDto[]>([]);
  // Свои MCP-серверы (раздел «Связи → свои MCP-серверы») — отдельная таблица от OAuth-каталога выше,
  // но показываем в той же секции формы, чтобы не вводить в путаницу.
  const [fMcpIds, setFMcpIds] = useState<string[]>([]);
  const [mcpCatalog, setMcpCatalog] = useState<McpServerDto[]>([]);
  const [foldTools, setFoldTools] = useState(true);
  const [openToolGroup, setOpenToolGroup] = useState<string | null>(null);
  const [skillSearch, setSkillSearch] = useState('');
  const [customSkill, setCustomSkill] = useState('');
  const [testInput, setTestInput] = useState('');

  useEffect(() => {
    agentsApi.list().then((r) => {
      const list = r || [];
      setAgents(list);
      setLoading(false);
      Promise.all(list.map((a) => integrationsApi.getAgentConnections(a.id)
        .then((res): [string, IntegrationConnectionDto[]] => [a.id, (res.connections || []).filter((c) => c.enabled)])
        .catch((): [string, IntegrationConnectionDto[]] => [a.id, []])))
        .then((pairs) => setAgentConns(Object.fromEntries(pairs)))
        .catch(() => {});
    }).catch(() => setLoading(false));
    pipelinesApi.list().then((r) => setPipes(r || [])).catch(() => {});
    skillsApi.list().then((r) => setAvailSkills(r || [])).catch(() => {});
    skillsApi.builtins().then((r) => setBuiltins(r || [])).catch(() => {});
  }, []);

  const toggle = (a: Agent) => { haptic('medium'); agentsApi.setEnabled(a.id, !a.enabled).then(() => { setAgents((prev) => prev.map((x) => x.id === a.id ? { ...x, enabled: !a.enabled } : x)); }).catch(() => {}); };
  const startEdit = (a: Agent) => { haptic('light'); setEditing(a.id); setCreating(false); setFName(a.name); setFDesc(a.description || ''); setFPrompt(a.system_prompt); setFTier(a.tier); setFSkills(a.skills || []); setFTools(a.enabled_tools || []); setFoldTools(true); setSkillSearch(''); setCustomSkill(''); setFConnIds([]); setConnCatalog([]); setFMcpIds([]); setMcpCatalog([]); integrationsApi.getAgentConnections(a.id).then((r) => { setConnCatalog(r.connections || []); setFConnIds(r.enabledConnectionIds || []); }).catch(() => {}); integrationsApi.listMcp().then((r) => setMcpCatalog(Array.isArray(r) ? r : [])).catch(() => setMcpCatalog([])); integrationsApi.getAgentIntegrations(a.id).then((r) => setFMcpIds(r.enabled_mcp_ids || [])).catch(() => {}); };
  const startCreate = () => { haptic('light'); setCreating(true); setEditing(null); setFName(''); setFDesc(''); setFPrompt(''); setFTier('mid'); setFSkills([]); setFTools(['web_search']); setFoldTools(true); setSkillSearch(''); setCustomSkill(''); setFConnIds([]); setConnCatalog([]); setFMcpIds([]); setMcpCatalog([]); integrationsApi.listConnections().then((r) => setConnCatalog(Array.isArray(r) ? r : [])).catch(() => setConnCatalog([])); integrationsApi.listMcp().then((r) => setMcpCatalog(Array.isArray(r) ? r : [])).catch(() => setMcpCatalog([])); };
  const cancelForm = () => { setCreating(false); setEditing(null); setConnCatalog([]); setFConnIds([]); setMcpCatalog([]); setFMcpIds([]); };
  const toggleSkill = (id: string) => { haptic('light'); setFSkills((prev) => prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]); };
  const toggleConn = (id: string) => { haptic('light'); setFConnIds((prev) => prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]); };
  const toggleMcp = (id: string) => { haptic('light'); setFMcpIds((prev) => prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]); };
  const addCustomSkill = () => { const v = customSkill.trim(); if (!v) return; haptic('light'); setFSkills((prev) => prev.includes(v) ? prev : [...prev, v]); setCustomSkill(''); };
  const genPrompt = async () => { if (!fName.trim() || !fDesc.trim()) return; haptic('light'); setGenLoading(true); try { const r = await agentsApi.generatePrompt({ name: fName.trim(), description: fDesc.trim() }); setFPrompt(r.prompt); haptic('medium'); } catch {} finally { setGenLoading(false); } };
  const save = async () => {
    if (!fName.trim()) return;
    haptic('medium'); setSaveLoading(true);
    try {
      let savedId: string | null = null;
      if (creating) {
        const a = await agentsApi.create({ name: fName.trim(), description: fDesc.trim(), system_prompt: fPrompt, tier: fTier, skills: fSkills, enabled_tools: fTools, enabled: true });
        setAgents((prev) => [...prev, a]); savedId = a.id;
      } else if (editing) {
        const a = await agentsApi.update(editing, { name: fName.trim(), description: fDesc.trim(), system_prompt: fPrompt, tier: fTier, skills: fSkills, enabled_tools: fTools });
        setAgents((prev) => prev.map((x) => x.id === editing ? a : x)); savedId = editing;
      }
      if (savedId && connCatalog.length > 0) { try { await integrationsApi.setAgentConnections(savedId, fConnIds); setAgentConns((prev) => ({ ...prev, [savedId as string]: connCatalog.filter((c) => fConnIds.includes(c.id)) })); } catch {} }
      if (savedId && mcpCatalog.length > 0) { try { await integrationsApi.setAgentIntegrations(savedId, 'mcp', fMcpIds); } catch {} }
      setEditing(null); setCreating(false); setConnCatalog([]); setFConnIds([]); setMcpCatalog([]); setFMcpIds([]);
    } catch {} finally { setSaveLoading(false); }
  };
  const askDelete = async (a: Agent) => {
    haptic('light');
    let pls: ImpactPipe[] = [];
    try { const r = await agentsApi.impact(a.id); pls = r.pipelines || []; } catch {}
    const single = pls.filter((p) => (p.memberCount ?? 1) < 2);
    const multi = pls.filter((p) => (p.memberCount ?? 1) >= 2);
    const initChoice: Record<string, 'delete' | 'rebuild'> = {};
    for (const p of multi) initChoice[p.id] = 'rebuild';
    setPipeChoice(initChoice);
    setConfirmDel({ agent: a, single, multi });
  };
  const setChoice = (pid: string, val: 'delete' | 'rebuild') => { haptic('light'); setPipeChoice((prev) => ({ ...prev, [pid]: val })); };
  const confirmDelete = async () => {
    if (!confirmDel) return; haptic('heavy'); setDeleting(true);
    const aid = confirmDel.agent.id;
    // Многоагентные сценарии: 'rebuild' (по умолчанию) — сохранить сценарий без агента; 'delete' — удалить целиком.
    const rebuildPipelineIds = confirmDel.multi.filter((p) => pipeChoice[p.id] !== 'delete').map((p) => p.id);
    try {
      await agentsApi.remove(aid, { rebuildPipelineIds });
      const [aRes, pRes] = await Promise.all([agentsApi.list(), pipelinesApi.list()]);
      setAgents(aRes || []);
      setPipes(pRes || []);
      setConfirmDel(null);
      setPipeChoice({});
    } catch {} finally { setDeleting(false); }
  };
  const test = async (a: Agent) => { haptic('medium'); setTesting(a.id); setTestResult(null); try { const r = await agentsApi.test(a.id, testInput || 'Hello'); setTestResult({ id: a.id, ok: true, msg: r.output }); } catch (e: any) { setTestResult({ id: a.id, ok: false, msg: e?.message || 'Ошибка' }); } finally { setTesting(null); } };
  const isForm = creating || editing !== null;

  // Встроенные инструменты: полный каталог с бэкенда (вкл/выкл каждый).
  const aliasToCanonical = new Map<string, string>();
  for (const b of builtins) { aliasToCanonical.set(b.id, b.id); for (const a of b.aliases || []) aliasToCanonical.set(a, b.id); }
  const builtinOn = (b: BuiltinTool) => fTools.some((t) => t === b.id || (b.aliases || []).includes(t));
  const toggleBuiltin = (b: BuiltinTool) => {
    haptic('light');
    setFTools((prev) => {
      const has = prev.some((t) => t === b.id || (b.aliases || []).includes(t));
      if (has) return prev.filter((t) => t !== b.id && !(b.aliases || []).includes(t));
      return [...prev, b.id];
    });
  };
  const enabledBuiltinCount = builtins.filter((b) => builtinOn(b)).length;
  const extraTools = fTools.filter((t) => !aliasToCanonical.has(t));
  const removeExtraTool = (id: string) => { haptic('light'); setFTools((prev) => prev.filter((t) => t !== id)); };

  // Навыки/интеграции: только не-builtin (http/mcp и кастомные).
  const otherSkills = availSkills.filter((s) => s.executor !== 'builtin');
  const knownSkillIds = new Set(otherSkills.map((s) => s.id));
  const extraSkills: Skill[] = fSkills.filter((id) => !knownSkillIds.has(id)).map((id) => ({ id, name: id, description: '', executor: 'http' } as Skill));
  const skillCatalog = [...otherSkills, ...extraSkills];
  const filteredSkills = skillCatalog.filter((s) => { const q = skillSearch.trim().toLowerCase(); return !q || (s.name || '').toLowerCase().includes(q) || s.id.toLowerCase().includes(q); });

  // Цепочка агентов сценария (agent_chain — порядок активации).
  const chainOf = (p: PipelineInstance): string[] => (Array.isArray(p.agent_chain) && p.agent_chain.length ? p.agent_chain : (p.agent_id ? [p.agent_id] : []));
  const agentById = new Map(agents.map((a) => [a.id, a] as const));
  const linkedGroups = pipes
    .map((p) => ({ pipeline: p, members: chainOf(p).map((aid) => agentById.get(aid)).filter((x): x is Agent => !!x) }))
    .filter((g) => g.members.length >= 1);
  const groupedIds = new Set<string>();
  linkedGroups.forEach((g) => g.members.forEach((m) => groupedIds.add(m.id)));
  const standaloneAgents = agents.filter((a) => !groupedIds.has(a.id));

  const renderAgentCard = (a: Agent, keyPrefix = '') => {
    const ck = keyPrefix + a.id;
    const open = openCard === ck;
    return (
    <div key={ck} className={'rf-card rf-agent' + (open ? ' open' : '')}>
      <div className="rf-agent-top rf-agent-head" onClick={() => { haptic('light'); setOpenCard(open ? null : ck); }}>
        <div className="rf-agent-ic"><Bot /></div>
        <div className="rf-agent-main">
          <div className="rf-agent-name">{a.name}</div>
          {a.description && <div className={'rf-agent-desc' + (open ? '' : ' rf-clamp1')}>{a.description}</div>}
        </div>
        <div className={'rf-switch' + (a.enabled ? ' on' : '')} onClick={(e) => { e.stopPropagation(); toggle(a); }} role="switch" aria-checked={a.enabled} />
        <ChevronDown className={'rf-agent-chev' + (open ? ' up' : '')} />
      </div>
      {open && (
        <>
          <div className="rf-agent-meta">
            <span className="rf-tag accent">{TIER_LABEL[a.tier] || a.tier}</span>
            <span className="rf-tag">{(a.enabled_tools || []).length} инстр.</span>
            <span className="rf-tag">{(a.skills || []).length} навык.</span>
            {(agentConns[a.id] || []).map((c) => {
              const label = PROVIDER_LABELS[c.provider] || c.provider;
              const acct = c.accountLabel && c.accountLabel !== label ? c.accountLabel : '';
              return (
                <span key={c.id} className="rf-tag ok rf-tag-conn" title={acct ? label + ' · ' + acct : label}>{label}{acct ? ' · ' + acct : ''}</span>
              );
            })}
          </div>
          {a.system_prompt && (
            <button className="rf-prompt-tog" onClick={() => setExpanded(expanded === ck ? null : ck)}>{expanded === ck ? <><ChevronUp />Скрыть промпт</> : <><ChevronDown />Показать промпт</>}</button>
          )}
          {expanded === ck && a.system_prompt && (
            <>
              <pre className="rf-pre">{a.system_prompt}</pre>
              <div className="rf-field rf-mt10"><label>Тест агента</label><input className="rf-input" value={testInput} onChange={(e) => setTestInput(e.target.value)} placeholder="Введите сообщение для теста..." /></div>
              <button className="rf-btn" onClick={() => test(a)} disabled={testing === a.id}>{testing === a.id ? <Loader2 className="spin" /> : <FlaskConical />}Запустить тест</button>
              {testResult && testResult.id === a.id && (<div className={'rf-result ' + (testResult.ok ? 'ok' : 'err')}>{testResult.ok ? <Sparkles /> : <AlertCircle />}<div>{testResult.msg}</div></div>)}
            </>
          )}
          <div className="rf-acts">
            <button className="rf-btn sm rf-grow" onClick={() => startEdit(a)}><Edit3 />Редактировать</button>
            <button className="rf-btn sm danger icon" title="Удалить" onClick={() => askDelete(a)}><Trash2 /></button>
          </div>
        </>
      )}
    </div>
    );
  };

  return (
    <div className="rf-detail">
      <div className="rf-topbar">
        <button className="rf-back" onClick={() => { haptic('light'); nav(-1 as any); }}><ArrowLeft />Назад</button>
        <div className="rf-topbar-title">Агенты</div>
        {!isForm && <button className="rf-back" onClick={startCreate}><Plus />Создать</button>}
      </div>
      <div className="rf-detail-scroll">
        {isForm ? (
          <>
            <div className="rf-info"><AlertCircle /><div><b>{creating ? 'Новый агент' : 'Редактирование агента'}</b><br />Агент — это ИИ-работник с ролью. Оркестратор делегирует ему задачи.</div></div>
            <div className="rf-field"><label>Имя</label><input className="rf-input" value={fName} onChange={(e) => setFName(e.target.value)} placeholder="Например: Аналитик данных" /></div>
            <div className="rf-field"><label>Описание</label><input className="rf-input" value={fDesc} onChange={(e) => setFDesc(e.target.value)} placeholder="Что делает этот агент" /></div>
            <div className="rf-field"><label>Системный промпт</label>
              <button className="rf-btn rf-mb12" onClick={genPrompt} disabled={!fName.trim() || !fDesc.trim() || genLoading}>{genLoading ? <Loader2 className="spin" /> : <Sparkles />}Сгенерировать промпт</button>
              <textarea className="rf-textarea" value={fPrompt} onChange={(e) => setFPrompt(e.target.value)} placeholder="Инструкции для агента..." rows={5} />
            </div>
            <div className="rf-field"><label>Уровень модели</label><select className="rf-select" value={fTier} onChange={(e) => setFTier(e.target.value as ModelTier)}>{TIERS.map((t) => <option key={t.value} value={t.value}>{t.label} — {t.desc}</option>)}</select></div>
            <div className="rf-field"><label>Встроенные инструменты</label>
              <button className="rf-fold" onClick={() => { haptic('light'); setFoldTools(!foldTools); }}><Wrench /><span>Доступные инструменты</span><span className="rf-fold-x rf-fold-c">{enabledBuiltinCount} вкл.</span>{foldTools ? <ChevronUp /> : <ChevronDown />}</button>
              {foldTools && (
                <div className="rf-foldbody">
                  <div className="rf-hint rf-mb8">Нажмите, чтобы включить или выключить инструмент.</div>
                  {builtins.length === 0 ? (<div className="rf-hint">Каталог инструментов недоступен</div>) : (
                    <div className="rf-toollist">
                      {builtins.filter((b) => !b.group).map((b) => { const on = builtinOn(b); return (
                        <button key={b.id} className={'rf-tooltog' + (on ? ' on' : '')} onClick={() => toggleBuiltin(b)}>
                          <span className={'rf-tooltog-box' + (on ? ' on' : '')}>{on ? <Check /> : null}</span>
                          <span className="rf-tooltog-text"><span className="rf-tooltog-name">{b.label}</span><span className="rf-tooltog-desc">{b.description}</span></span>
                        </button>
                      ); })}
                      {Array.from(new Set(builtins.filter((b) => b.group).map((b) => b.group as string))).map((gid) => {
                        const items = builtins.filter((b) => b.group === gid);
                        const groupLabel = items[0]?.groupLabel || gid;
                        const groupDesc = items[0]?.groupDescription || '';
                        const onCount = items.filter((b) => builtinOn(b)).length;
                        const isOpen = openToolGroup === gid;
                        return (
                          <div key={gid} className="rf-toolgroup">
                            <button className={'rf-tooltog rf-toolgroup-head' + (onCount > 0 ? ' on' : '')} onClick={() => { haptic('light'); setOpenToolGroup(isOpen ? null : gid); }}>
                              <span className={'rf-tooltog-box' + (onCount > 0 ? ' on' : '')}>{onCount > 0 ? <Check /> : null}</span>
                              <span className="rf-tooltog-text"><span className="rf-tooltog-name">{groupLabel}{onCount > 0 ? ` \u00b7 ${onCount}/${items.length} вкл.` : ''}</span><span className="rf-tooltog-desc">{groupDesc}</span></span>
                              {isOpen ? <ChevronUp /> : <ChevronDown />}
                            </button>
                            {isOpen && (
                              <div className="rf-toolgroup-body">
                                {items.map((b) => { const on = builtinOn(b); return (
                                  <button key={b.id} className={'rf-tooltog rf-tooltog-sub' + (on ? ' on' : '')} onClick={() => toggleBuiltin(b)}>
                                    <span className={'rf-tooltog-box' + (on ? ' on' : '')}>{on ? <Check /> : null}</span>
                                    <span className="rf-tooltog-text"><span className="rf-tooltog-name">{b.label}</span><span className="rf-tooltog-desc">{b.description}</span></span>
                                  </button>
                                ); })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {extraTools.length > 0 && (<>
                    <div className="rf-seclabel rf-mt10">Другие инструменты</div>
                    <div className="rf-chipgrid">{extraTools.map((id) => (<button key={id} className="rf-chiptog on" onClick={() => removeExtraTool(id)}><X />{id}</button>))}</div>
                  </>)}
                </div>
              )}
            </div>
            <div className="rf-field"><label>Навыки и интеграции</label>
              <div className="rf-hint rf-mb12">Подключённые кастомные навыки и MCP. Выбрано: {fSkills.length}</div>
              {skillCatalog.length > 8 && <input className="rf-input rf-mb12" placeholder="Поиск по навыкам..." value={skillSearch} onChange={(e) => setSkillSearch(e.target.value)} />}
              {filteredSkills.length === 0 ? (
                <div className="rf-hint">{skillCatalog.length === 0 ? 'Навыков пока нет — добавьте свой ниже' : 'Ничего не найдено'}</div>
              ) : (
                <div className="rf-chipgrid">
                  {filteredSkills.map((s) => { const on = fSkills.includes(s.id); return (
                    <button key={s.id} className={'rf-chiptog' + (on ? ' on' : '')} onClick={() => toggleSkill(s.id)} title={s.description || s.id}>{on ? <Check /> : <Plus />}{s.name || s.id}</button>
                  ); })}
                </div>
              )}
              <div className="rf-inline rf-mt10"><input className="rf-input" placeholder="Свой навык (id)" value={customSkill} onChange={(e) => setCustomSkill(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomSkill(); } }} /><button className="rf-btn icon" onClick={addCustomSkill}><Plus /></button></div>
            </div>
            <div className="rf-field"><label>Подключения (MCP / OAuth)</label>
              <div className="rf-hint rf-mb12">Сервисы, аккаунты и свои MCP-серверы из раздела «Связи». Выберите нужные для этого агента. Выбрано: {fConnIds.length + fMcpIds.length}</div>
              {connCatalog.filter((c) => c.status === 'connected' || c.status === 'demo').length === 0 && mcpCatalog.length === 0 ? (
                <div className="rf-hint">Нет подключённых сервисов — подключите их в разделе «Связи»</div>
              ) : (
                <div className="rf-chipgrid">
                  {connCatalog.filter((c) => c.status === 'connected' || c.status === 'demo').map((c) => { const on = fConnIds.includes(c.id); const label = PROVIDER_LABELS[c.provider] || c.provider; const acct = c.accountLabel && c.accountLabel !== label ? c.accountLabel : ''; return (
                    <button key={c.id} className={'rf-chiptog' + (on ? ' on' : '')} onClick={() => toggleConn(c.id)} title={acct ? label + ' · ' + acct : label}>{on ? <Check /> : <Plus />}{label}{acct ? ' · ' + acct : ''}{c.status === 'demo' ? ' (демо)' : ''}</button>
                  ); })}
                  {mcpCatalog.map((m) => { const on = fMcpIds.includes(m.id); const broken = m.last_error && !m.last_ok_at; return (
                    <button key={'mcp:' + m.id} className={'rf-chiptog' + (on ? ' on' : '')} onClick={() => toggleMcp(m.id)} title={m.url}>{on ? <Check /> : <Plus />}{m.name}{!m.enabled ? ' (отключен)' : broken ? ' (ошибка)' : ''}</button>
                  ); })}
                </div>
              )}
            </div>
            <div className="rf-btn-row"><button className="rf-btn primary" onClick={save} disabled={!fName.trim() || saveLoading}>{saveLoading ? <Loader2 className="spin" /> : <Save />}Сохранить</button><button className="rf-btn" onClick={cancelForm}><X />Отмена</button></div>
          </>
        ) : loading ? (
          <div className="rf-loading"><div className="rf-spin" /><p>Загрузка…</p></div>
        ) : agents.length === 0 ? (
          <div className="rf-empty"><div className="rf-empty-ic"><Bot /></div><h3>Агентов нет</h3><p>Создайте первого агента кнопкой «Создать»</p></div>
        ) : (
          <div className="rf-rows rf-stagger">
            {linkedGroups.length > 0 && <div className="rf-seclabel">Связаны в сценариях</div>}
            {linkedGroups.map((g) => (
              <div key={'grp-' + g.pipeline.id} className="rf-linkgroup">
                <button className="rf-linkgroup-head" onClick={() => { haptic('light'); nav('/automation/' + g.pipeline.id); }}>
                  <Network /><span className="rf-linkgroup-name">{g.pipeline.name || 'Сценарий'}</span><span className="rf-linkgroup-cnt">{g.members.length === 1 ? 'агент сценария' : g.members.length + ' по очереди'}</span>
                </button>
                {g.members.map((m, i) => (
                  <div key={g.pipeline.id + ':' + m.id} className="rf-linkitem">
                    <div className="rf-linkrail"><span className="rf-linkstep">{i + 1}</span>{i < g.members.length - 1 && <span className="rf-linkline" />}</div>
                    <div className="rf-linkbody">{renderAgentCard(m, g.pipeline.id + ':')}</div>
                  </div>
                ))}
              </div>
            ))}
            {linkedGroups.length > 0 && standaloneAgents.length > 0 && <div className="rf-seclabel">Отдельные агенты</div>}
            {standaloneAgents.map((a) => renderAgentCard(a))}
          </div>
        )}
      </div>
      <ConfirmSheet
        open={!!confirmDel}
        title="Удалить агента?"
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => { if (!deleting) setConfirmDel(null); }}
      >
        {confirmDel && (
          <>
            Агент <b>«{confirmDel.agent.name}»</b> будет удалён безвозвратно.
            {confirmDel.single.length > 0 && (
              <>
                <br /><br />Эти сценарии используют только этого агента и будут удалены ({confirmDel.single.length}):
                <ul className="rf-confirm-list">{confirmDel.single.map((pp) => <li key={pp.id}>{pp.name || 'Без названия'}{pp.enabled ? '' : ' (выключен)'}</li>)}</ul>
              </>
            )}
            {confirmDel.multi.length > 0 && (
              <>
                <br /><br />В этих сценариях несколько агентов. Выберите, что сделать с каждым:
                <div className="rf-scochoice-list">
                  {confirmDel.multi.map((pp) => {
                    const ch = pipeChoice[pp.id] || 'rebuild';
                    return (
                      <div key={pp.id} className="rf-scochoice">
                        <div className="rf-scochoice-name">{pp.name || 'Без названия'} · {pp.memberCount} агентов{pp.enabled ? '' : ' (выключен)'}</div>
                        <div className="rf-scochoice-opts">
                          <button type="button" className={'rf-chiptog' + (ch === 'rebuild' ? ' on' : '')} onClick={() => setChoice(pp.id, 'rebuild')}>{ch === 'rebuild' ? <Check /> : null}Пересобрать без агента</button>
                          <button type="button" className={'rf-chiptog' + (ch === 'delete' ? ' on' : '')} onClick={() => setChoice(pp.id, 'delete')}>{ch === 'delete' ? <Check /> : null}Удалить сценарий</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
            {confirmDel.single.length === 0 && confirmDel.multi.length === 0 && (
              <><br /><br />Он не используется ни в одном сценарии.</>
            )}
          </>
        )}
      </ConfirmSheet>
    </div>
  );
}
